/**
 * BT-10740: password managers and browser autofill are OFF for every field;
 * supporting them is the exception. The only fields that opt in are the
 * sign-in and password forms, by carrying the `ALLOW_AUTOFILL_ATTR` marker on
 * their container (`<form data-allow-autofill>`). Nothing else needs — or
 * should add — any autofill attribute of its own.
 *
 * There's no single standard, so each field gets every manager's opt-out:
 *   - `autocomplete="off"` — browser autofill (Chrome partly ignores it, hence the rest)
 *   - `data-1p-ignore`     — 1Password
 *   - `data-lpignore`      — LastPass
 *   - `data-bwignore`      — Bitwarden
 *   - `data-form-type`     — Dashlane (`other` = not a login/identity form)
 */
export const ALLOW_AUTOFILL_ATTR = 'data-allow-autofill'

const NO_AUTOFILL_ATTRS = Object.freeze({
  autocomplete: 'off',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const)

// Inputs a password manager could fill; buttons, checkboxes, files, etc. aren't.
const NON_TEXT_INPUT_TYPES = new Set([
  'hidden', 'submit', 'button', 'reset', 'image', 'checkbox', 'radio', 'file', 'range', 'color',
])

const isFillable = (el: Element): el is HTMLInputElement | HTMLTextAreaElement => {
  if (el instanceof HTMLTextAreaElement) return true
  return el instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(el.type)
}

// Sets only what differs, so re-applying after an attribute change can't loop.
const optOut = (el: HTMLInputElement | HTMLTextAreaElement) => {
  if (el.closest(`[${ALLOW_AUTOFILL_ATTR}]`)) return
  Object.entries(NO_AUTOFILL_ATTRS).forEach(([name, value]) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value)
  })
}

const optOutWithin = (node: Node) => {
  if (!(node instanceof Element)) return
  if (isFillable(node)) optOut(node)
  node.querySelectorAll('input, textarea').forEach((el) => { if (isFillable(el)) optOut(el) })
}

/**
 * Opt every text input and textarea under `root` out of password managers and
 * browser autofill — those on the page now, any added later, and any whose
 * opt-out (or type) is changed afterwards — except inside an
 * `ALLOW_AUTOFILL_ATTR` container. Call once at app start, before render;
 * returns a function that stops watching.
 */
export function installNoAutofillByDefault(root: Element = document.documentElement): () => void {
  optOutWithin(root)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type === 'attributes') optOutWithin(m.target)
      else m.addedNodes.forEach(optOutWithin)
    })
  })
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['type', ...Object.keys(NO_AUTOFILL_ATTRS)],
  })
  return () => observer.disconnect()
}
